import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeleteAccountModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDeleteAccount = async () => {
    // Validation
    if (!password) {
      toast.error('Bitte geben Sie Ihr Passwort ein');
      return;
    }

    if (confirmText !== 'LÖSCHEN') {
      toast.error('Bitte geben Sie "LÖSCHEN" ein, um zu bestätigen');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/user/delete-account`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { password }
        }
      );

      toast.success('Ihr Konto wurde erfolgreich gelöscht');

      // Clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);

    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Kontos');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPassword('');
      setConfirmText('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            Konto löschen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-900 font-semibold mb-2">
              ⚠️ Warnung: Diese Aktion ist endgültig!
            </p>
            <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
              <li>Alle Ihre Daten werden gelöscht</li>
              <li>Aktive Kampagnen laufen bis zum Ende des aktuellen Abrechnungszyklus und werden danach automatisch gestoppt</li>
              <li>Aktive Abonnements werden gekündigt</li>
              <li>Diese Aktion kann nicht rückgängig gemacht werden</li>
            </ul>
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <Label htmlFor="delete-password">Passwort zur Bestätigung</Label>
            <Input
              id="delete-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ihr Passwort eingeben"
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {/* Confirmation Text */}
          <div className="space-y-2">
            <Label htmlFor="confirm-text">
              Geben Sie "LÖSCHEN" ein, um zu bestätigen
            </Label>
            <Input
              id="confirm-text"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="LÖSCHEN"
              disabled={loading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={loading || !password || confirmText !== 'LÖSCHEN'}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {loading ? 'Wird gelöscht...' : 'Konto löschen'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountModal;
